import _ from 'lodash';
import { resolutions } from '../../constants';

export const isChrome = /Google Inc/.test(navigator?.vendor);
export const permissionAPI = !!(navigator && navigator.permissions && isChrome);

export const frameRateTrials = [29.97, 24, undefined];
export const duplicateObject = (obj) => {
	return obj ? JSON.parse(JSON.stringify(obj)) : {};
};

export const generateConstraints = (
	resolution = 1080,
	settings = {
		echoCancellation: false,
		noiseSuppression: false,
		frameRate: 24,
		frameRateRequirement: 'exact',
	}
) => {
	let temp = {};
	temp.audio = {
		autoGainControl: { ideal: false },
		echoCancellation: { ideal: settings.echoCancellation },
		// noisesuppresion is only the first value you set. so first call has to be the proper one
		noiseSuppression: { ideal: settings.noiseSuppression },
		sampleSize: { ideal: 16 },
		channelCount: { ideal: 1 },
		sampleRate: { ideal: 48000 },
	};

	temp.video = {
		width: { ideal: resolutions[resolution] },
		height: { ideal: resolution },
		facingMode: { ideal: 'user' },
		// aspectRatio: 1.7777777778,
		frameRate: { [settings.frameRateRequirement]: settings.frameRate },
	};

	return temp;
};

export const injectDeviceConstraints = (constraints, deviceId, kind) => {
	const nextConstraints = duplicateObject(constraints);
	const constraintKey = kind === 'audioinput' ? 'audio' : 'video';
	if (nextConstraints[constraintKey] !== false) {
		if (nextConstraints[constraintKey] === true) {
			nextConstraints[constraintKey] = { deviceId: { exact: deviceId } };
		} else {
			nextConstraints[constraintKey].deviceId = { exact: deviceId };
		}
	} else {
		// Constraint was false, won't add deviceId.
	}
	return nextConstraints;
};

export const storageEstimateWrapper = () => {
	if ('storage' in navigator && 'estimate' in navigator.storage) {
		// We've got the real thing! Return its response.
		return navigator.storage.estimate();
	}
	if ('webkitTemporaryStorage' in navigator && 'queryUsageAndQuota' in navigator.webkitTemporaryStorage) {
		// Return a promise-based wrapper that will follow the expected interface.
		return new Promise(function (resolve, reject) {
			navigator.webkitTemporaryStorage.queryUsageAndQuota(function (usage, quota) {
				resolve({ usage: usage, quota: quota });
			}, reject);
		});
	}
	// If we can't estimate the values, return a Promise that resolves with NaN.
	return Promise.resolve({ usage: NaN, quota: NaN });
};

export const checkSettingsAlerts = async (uploadOnly = false, test = false, browserResults) => {
	let alerts = [];
	let percentUsed = null;

	//https://stackoverflow.com/questions/3514784/what-is-the-best-way-to-detect-a-mobile-device
	if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || test) {
		alerts.push('incompatibleDevice');
	}

	if (browserResults) {
		if (browserResults.status === 'UNSUPPORTED') {
			alerts.push('incompatibleBrowser');
		} else if (browserResults.status === 'UNTESTED') {
			alerts.push('untestedBrowser');
		}
	}

	if (!('MediaRecorder' in window) || test) {
		alerts.push('browserSupport');
	} else if (!('indexedDB' in window) || test) {
		alerts.push('indexDB');
	}

	// Pass uploadOnly as input...
	if (!uploadOnly) {
		await storageEstimateWrapper().then((res) => {
			const { usage, quota } = res;
			const pctUsed = Math.round((usage / quota) * 100);
			const usageInMib = Math.round(usage / (1024 * 1024));
			const quotaInMib = Math.round(quota / (1024 * 1024));

			const details = `${usageInMib} out of ${quotaInMib} MiB used (${percentUsed}%)`;

			percentUsed = pctUsed;
			const spaceAvailable = quotaInMib - usageInMib;
			if ((spaceAvailable < 200 && settings.recordLocalVideo) || test) {
				alerts.push('storageSpace');
			}
		});
	}
	return { alerts: alerts, percentUsed: percentUsed };
};

const limitedConstraints = ['deviceId'];

/**
 * Filters constraints that can't be used on
 * tracks
 */
export const filterTrackConstraints = (constraints) => {
	return _.omit(constraints, limitedConstraints);
};

/**
 * NOT USED: Determine if the differences between constraints A & B need MediaStream regeneration.
 * @param {*} constraintsA
 * @param {*} constraintsB
 */
export const needsRegeneration = (constraintsA, constraintsB) => {
	const differences = { audio: [], video: [] };
	let result = false;

	if (
		typeof constraintsA.audio !== 'boolean' &&
		typeof constraintsB.audio !== 'boolean' &&
		typeof constraintsA.video !== 'boolean' &&
		typeof constraintsB.video !== 'boolean'
	) {
		if (!_.isEqual(constraintsA.audio, constraintsB.audio)) {
			const keys = _.uniq(Object.keys(constraintsA.audio), Object.keys(constraintsB.audio));
			keys.forEach((key) => {
				if (limitedConstraints.includes(key) && !_.isEqual(constraintsA.audio[key], constraintsB.audio[key])) {
					differences.audio.push(key);
				}
			});
		}

		if (!_.isEqual(constraintsA.video, constraintsB.video)) {
			const keys = _.uniq(Object.keys(constraintsA.video), Object.keys(constraintsB.video));
			keys.forEach((key) => {
				if (limitedConstraints.includes(key) && !_.isEqual(constraintsA.video[key], constraintsB.video[key])) {
					differences.video.push(key);
				}
			});
		}

		result = differences.video.length > 0 || differences.audio.length > 0;
	} else {
		result = true;
	}

	return result;
};

Promise.wait = function (ms) {
	return new Promise(function (resolve) {
		setTimeout(resolve, ms);
	});
};

Promise.prototype.timeout = function (ms) {
	return Promise.race([
		this,
		Promise.wait(ms).then(function () {
			throw new Error('time out');
		}),
	]);
};
